import type React from 'react';

import classNames from 'classnames';
import { type ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { MutedIcon } from '../../icons/muted';
import { VolumeIcon } from '../../icons/volume';
import { isArrayOfNumbers } from '../../utils/is-array-of-numbers';
import { Tooltip } from '../tooltip/tooltip';

import styles from './volume.module.css';

type Props = {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  isMuted?: boolean;
  showVolumeSlider?: boolean;
  verticalVolumeSlider?: boolean;
  className?: string;
  onChange?: (value: number) => void;
  onMuteChange?: (value: boolean) => void;
};

export function Volume({
  min = 0,
  max = 1,
  step = 0.1,
  value = 0,
  isMuted = false,
  onChange,
  onMuteChange,
  showVolumeSlider = true,
  verticalVolumeSlider = false,
  className,
}: Props) {
  const { t } = useTranslation();

  if (!isArrayOfNumbers([min, max, step, value])) return null;

  const position = isMuted ? 0 : max - min <= 0 ? 0 : ((value - min) * 100) / (max - min);
  const background = `linear-gradient(to right, var(--currentVolumeColor) ${position}%, var(--sliderBackgroundColor) 0)`;

  const handleMuteClick = () => {
    onMuteChange?.(!isMuted);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.valueAsNumber);
  };

  return (
    <div className={classNames(className, styles.wrapper)}>
      <VolumeButton isMuted={isMuted} onClick={handleMuteClick} hideTooltip={verticalVolumeSlider} />

      {showVolumeSlider && (
        <div className={classNames(styles.sliderWrapper, verticalVolumeSlider && styles.vertical)}>
          <input
            type="range"
            name="volume-slider"
            data-testid="volume-slider"
            aria-label={t('ui.volume.slider.label')}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            min={min}
            max={max}
            step={step}
            value={isMuted ? 0 : value}
            onChange={handleChange}
            className={classNames(styles.rangeInput, styles.input)}
            style={{ background }}
          />
        </div>
      )}
    </div>
  );
}

type VolumeButtonProps = {
  isMuted: boolean;
  hideTooltip?: boolean;
  onClick: () => void;
};

function VolumeButton({ isMuted, onClick, hideTooltip = false }: VolumeButtonProps) {
  const { t } = useTranslation();

  const label = isMuted ? t('ui.volume.mute-button.label.active') : t('ui.volume.mute-button.label.inactive');
  const button = useCallback(
    (ref: React.LegacyRef<HTMLButtonElement>, props: Record<string, unknown>) => (
      <button
        type="button"
        data-testid="mute-button"
        name="mute-button"
        className={styles.button}
        onClick={onClick}
        aria-label={label}
        ref={ref}
        {...props}
      >
        {isMuted ? <MutedIcon /> : <VolumeIcon />}
      </button>
    ),
    [isMuted, label, onClick]
  );
  return <Tooltip tooltip={label} trigger={button} hideTooltip={hideTooltip} />;
}
